import { useCallback, useState } from "react";
import { logError, logEvent, scrubMessage } from "../../../../../telemetry";

/**
 * State + action runner for the Format Tools feature (AIVA-5405).
 * `run` wraps an operation with busy/status/error handling so the panel stays
 * declarative.
 *
 * Usage/error telemetry: each completed action emits `connector.action.invoke`
 * and each failure emits `error.connector.failure`, tagged for Deckorate — so
 * Deckorate usage and uncaught-in-action errors surface in AiVA Studio through
 * the shared add-in pipeline, the same way other connector features do.
 */
export function useFormatTools() {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (label: string, action: () => Promise<void> | void) => {
      setError(null);
      setBusy(true);
      setStatus(null);
      try {
        await action();
        setStatus(`${label} applied`);
        logEvent("connector.action.invoke", "usage", { connectorSystemName: "deckorate" });
      } catch (e) {
        const msg = e instanceof Error ? e.message : `${label} failed`;
        setError(msg);
        logError(
          "error.connector.failure",
          {
            type: e instanceof Error ? e.name : "DeckorateFormatToolsError",
            scrubbedMessage: scrubMessage(msg),
          },
          { connectorSystemName: "deckorate" },
        );
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  return { busy, status, error, run };
}

export type FormatTools = ReturnType<typeof useFormatTools>;
